import { forwardRef } from 'react';
import {
  OutlinedInput,
  InputAdornment,
  StyledEngineProvider,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as CommonIcons from 'assets/common';
import {
  classNames,
  extractNumberInput,
  getDataPropsFromRest,
  isNullOrUndefined,
} from 'utilities/Utils';
import { useController } from 'react-hook-form';

// type defination for TextField Props

type TextFieldProps = Pick<
React.ComponentProps<typeof OutlinedInput>,
| 'autoFocus'
| 'disabled'
| 'error'
| 'inputRef'
| 'onBlur'
| 'placeholder'
| 'required'
| 'type'
| 'value'
| 'onClick'
| 'readOnly'
| 'style'
| 'name'
| 'onFocusCapture'
>;

type InputProps = TextFieldProps & {
  disableClear?: boolean;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  size?: 'small' | 'medium';
  orientation?: 'left' | 'right' | 'center';
  min?: number;
  max?: number;
  charLimit?: number;
  decimalPoints?: number;
  skipNumericCast?: boolean;
  onChange?: (value: string, name: string) => void;
  format?: (value: string) => string;
  prefix?: string;
  suffix?: string;
  className?: string;
  maskValue?: boolean;
  variant?: 'small' | 'default';
  convertToUppercase?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  elementClassNames?: string;
  readValueOnChange?: (val) => void;
};

// end of type defination for TextField Props

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '40px',
    overflow: 'hidden',
    borderRadius: '4px',
    fontFamily: 'Roboto',
    fontSize: '14px !important',
    backgroundColor: theme.palette.common.white,
    '& input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button':
      {
        appearance: 'none',
        margin: 0,
      },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.backgrounds.dark,
      color: '#404040 !important',
    },
    '&.Mui-disabled ,&.Mui-disabled > input ': {
      color: '#404040 !important',
    },
  },
  endCloseIcon: {
    marginRight: '12px',
    width: '20px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.backgrounds.dark,
      borderRadius: '12px',
      padding: '3px',
      transition: 'all 0.2s',
    },
  },
  small: {
    width: '182px',
  },
  variantsm: {
    width: '60px',
    height: '24px !important',
    fontSize: '14px !important',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    '& input': {
      height: '24px',
      padding: '0 4px !important',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      height: '24px !important',
      top: '0px',
      '& legend': {
        display: 'none',
      },
    },
  },
  prefixSuffix: {
    backgroundColor: '#EFEFEF',
    padding: '10px',
    color: '#787878',
    fontSize: '14px !important',
  },
  adornedStart: {
    paddingLeft: '0px',
  },
  adornedEnd: { paddingRight: '0px' },
  input: (props: { orientation: 'left' | 'right' | 'center' }) => ({
    '&:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px white inset',
    },
    textAlign: props.orientation,
  }),
}));

const InputComp = (props: InputProps, ref?: React.Ref<HTMLDivElement>) => {
  const {
    type = 'text',
    placeholder = '',
    disabled = false,
    value = '',
    onChange,
    onBlur,
    disableClear = true,
    leftElement,
    rightElement,
    error = false,
    format,
    onClick,
    readOnly = false,
    style,
    orientation = 'left',
    size = 'medium',
    name = '',
    autoFocus = false,
    min = 0, //For type number
    max, //For type number,
    charLimit,
    decimalPoints = 2,
    skipNumericCast = false,
    prefix = '',
    suffix = '',
    className = '',
    maskValue = false,
    variant = 'default',
    onFocusCapture,
    convertToUppercase = false,
    readValueOnChange = val => val,
    onKeyDown,
    elementClassNames,
    ...rest
  } = props;
  const classes = useStyles({ orientation });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name: targetName } = event.target;

    if (convertToUppercase) {
      const val = event.target.value.toUpperCase();
      readValueOnChange(val);
      return onChange(val, targetName);
    }

    // Check if maskValue is true and mask the value
    if (maskValue) {
      const maskedValue = '*'.repeat(event.target.value.length);
      readValueOnChange(maskedValue);
      return onChange(maskedValue, targetName);
    }
    if (type === 'text' || type === 'password' || skipNumericCast) {
      const val = event.target.value;
      readValueOnChange(val);
      return onChange(val, targetName);
    }

    // Check for numerical value only and non negative
    const numberInput = extractNumberInput(event, decimalPoints) as string;
    onChange(numberInput, targetName);
    readValueOnChange(numberInput);
  };

  const handleOnWheel = e => {
    // to disable changing values on scroll when input type is number
    if (type === 'number' && e.target instanceof HTMLElement) {
      e.target.blur();
    }
  };
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent the input of the dash character
    if (type === 'number' && event.key === '-') {
      event.preventDefault();
    }
  };

  const dataTestIdProps = getDataPropsFromRest(rest);

  return (
    <StyledEngineProvider injectFirst>
      <OutlinedInput
        autoFocus={autoFocus}
        name={name}
        type={type}
        inputRef={ref}
        className={classNames(
          classes.root,
          size === 'small' ? classes.small : '',
          variant === 'small' ? classes.variantsm : '',
          className,
        )}
        onWheel={handleOnWheel}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleChange}
        onBlur={onBlur}
        onFocusCapture={onFocusCapture}
        onClick={onClick}
        value={format ? format(value as string) : value}
        endAdornment={
          rightElement ? (
            <InputAdornment position="end">
              <div className="pr-3">{rightElement}</div>
              {suffix && <div className={classes.prefixSuffix}>{suffix}</div>}
            </InputAdornment>
          ) : !disableClear ? (
            <InputAdornment position="end">
              <CommonIcons.Close
                className={classes.endCloseIcon}
                onClick={() => (onChange ? onChange('', name) : null)}
              />
              {suffix && <div className={classes.prefixSuffix}>{suffix}</div>}
            </InputAdornment>
          ) : suffix ? (
            <InputAdornment position="end">
              {suffix && <div className={classes.prefixSuffix}>{suffix}</div>}
            </InputAdornment>
          ) : null
        }
        startAdornment={
          (leftElement || prefix) && (
            <InputAdornment position="start">
              {prefix && <div className={classes.prefixSuffix}>{prefix}</div>}
              {leftElement && (
                <div
                  className={`flex items-center ${
                    !prefix && variant !== 'small' ? 'pl-3' : ''
                  } ${elementClassNames}`}
                >
                  {leftElement}
                </div>
              )}
            </InputAdornment>
          )
        }
        inputProps={{
          min,
          max,
          maxLength: charLimit,
          ...dataTestIdProps,
          className: classes.input,
          style: {
            textAlign: orientation,
          },
        }}
        error={error}
        readOnly={readOnly}
        style={style}
        classes={{
          adornedStart: classes.adornedStart,
          adornedEnd: classes.adornedEnd,
        }}
        sx={{
          '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: 'black',
          },
        }}
        onKeyDown={onKeyDown}
        onKeyPress={handleKeyPress}
      />
    </StyledEngineProvider>
  );
};

const Input = forwardRef(InputComp);

function ControlledInput(props) {
  const {
    name,
    control,
    defaultValue,
    isRequired,
    customValidator,
    pattern,
    patternErrorMessage = null,
    maskValue,
    convertToUppercase,
    className = '',
    valueAsNumber,
    setValueAs,
    ...additional
  } = props;

  const validations: Record<string, unknown> = {
    required: {
      value: isRequired,
      message: 'This field is required',
    },
  };

  if (typeof customValidator === 'function') {
    validations.validate = customValidator;
  }
  if (typeof valueAsNumber !== 'undefined') {
    validations.valueAsNumber = valueAsNumber;
  }
  if (typeof setValueAs !== 'undefined') {
    validations.setValueAs = setValueAs;
  }

  if (!isNullOrUndefined(pattern)) {
    validations.pattern = {
      value: pattern,
      message: patternErrorMessage || 'Please enter info in correct format',
    };
  }

  const { min, max } = additional;
  if (!isNullOrUndefined(min)) {
    validations.min = {
      value: min,
      message: `The value cannot be lesser than ${min}`,
    };
  }
  if (!isNullOrUndefined(max)) {
    validations.max = {
      value: max,
      message: `The value cannot be greater than ${max}`,
    };
  }

  const {
    field: { ref, value, onChange, onBlur },
  } = useController({
    name,
    control,
    defaultValue,
    rules: validations,
  });

  return (
    <StyledEngineProvider injectFirst>
      <Input
        ref={ref}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        maskValue={maskValue}
        convertToUppercase={convertToUppercase}
        {...additional}
        className={className}
      />
    </StyledEngineProvider>
  );
}

export { Input, ControlledInput };
